import React from "react";

import {
	Picto,
	TTableColumn,
	TTableItemAction,
} from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";

import { Dot } from "@/components/atoms/Dot/Dot";

import { TViewSummary } from "@/types";

const formatLastTask = (date: string | null) =>
	date ? format(new Date(date), "d MMM yyyy, HH:mm") : "—";

/**
 * The /views table. Functions, not constants: `t()` is i18next's
 * plain export, so the caller runs them after i18next.init() (inside
 * the component), or every label resolves to its raw key.
 */
export const VIEW_LIST_COLUMNS = (): TTableColumn<TViewSummary>[] => [
	{ key: "contextMenu" },
	{
		key: "name",
		label: t("views.table.name"),
		sortable: true,
		width: "2fr",
		truncate: false,
		render: (view) => (
			<span className="flex items-center gap-3 py-1">
				<Dot color={view.color} />
				<span className="flex min-w-0 flex-col">
					<span className="flex items-center gap-1.5 truncate font-medium">
						{view.name}
						{view.isArchived && (
							<span title={t("views.archived")}>
								<Picto
									icon="archive"
									className="h-3 w-3 text-neutral-400"
								/>
							</span>
						)}
					</span>
					{view.description && (
						<span className="truncate text-xs text-neutral-500">
							{view.description}
						</span>
					)}
				</span>
			</span>
		),
	},
	{
		key: "projectsCount",
		label: t("views.table.projects"),
		sortable: true,
		width: "auto",
	},
	{
		key: "workedHours",
		label: t("views.table.hours"),
		width: "auto",
		truncate: false,
		render: (view) => (
			<span>
				<span className="font-medium">{view.workedHours.str}</span>
				{view.expectedHours.raw > 0 && (
					<span className="text-neutral-500">
						{" "}
						/ {view.expectedHours.str}
					</span>
				)}
			</span>
		),
	},
	{
		key: "lastTaskDate",
		label: t("views.table.lastTask"),
		sortable: true,
		width: "auto",
		render: (view) => formatLastTask(view.lastTaskDate),
	},
	{
		key: "isEverything",
		label: t("views.table.selection"),
		width: "auto",
		truncate: false,
		render: (view) =>
			view.isEverything ? (
				<span className="flex items-center gap-1.5 text-neutral-600">
					<Picto icon="eye" className="h-3.5 w-3.5" />
					{t("views.everything")}
				</span>
			) : (
				<span className="text-neutral-600">
					{t("views.table.selectionCount", {
						projects: view.projects.length,
						clients: view.clients.length,
					})}
				</span>
			),
	},
];

export type TViewListRowHandlers = {
	onEdit: (view: TViewSummary) => void;
	onArchive: (view: TViewSummary) => void;
	onDelete: (view: TViewSummary) => void;
};

/** The row menu; a view is personal, so only the archive state hides anything. */
export const VIEW_LIST_ROW_ACTIONS = ({
	onEdit,
	onArchive,
	onDelete,
}: TViewListRowHandlers): TTableItemAction<TViewSummary>[] => [
	{
		label: t("actions.edit"),
		picto: "pen",
		onClick: onEdit,
	},
	{
		label: t("actions.archive"),
		picto: "archive",
		hidden: (view: TViewSummary) => !!view.isArchived,
		onClick: onArchive,
	},
	{
		label: t("actions.unarchive"),
		picto: "archive",
		hidden: (view: TViewSummary) => !view.isArchived,
		onClick: onArchive,
	},
	{
		label: t("actions.delete"),
		picto: "trash",
		onClick: onDelete,
	},
];
